/**
 * Badge API hooks for managing badges
 * Provides React Query hooks for badge CRUD operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  ApiHttpError,
  authenticatedApi,
  encodeRfc3986,
} from '../lib/authenticated-api';
import toast from 'react-hot-toast';
import type {
  BadgeDefinition,
  BadgeListResponse,
  BadgeAuditResponse,
} from '../lib/types/badge';

// Badge input types
//
// Neither shape carries `icon_url` or `icon_updated_at`. Those are written only
// by `POST /v1/admin/badges/:badgeId/icon`, which sanitises the image it
// stores; the JSON endpoints reject them with 400. Keeping them off the request
// types means a caller cannot reintroduce them by accident.
export interface CreateBadgeInput {
  badgeId: string;
  name: string;
  description: string;
  // Create-only, and permanent: `UpdateBadgeInput` below has no counterpart
  // because the API rejects it on PUT. Correcting a mistake means deleting the
  // badge, which removes it from everyone holding it.
  imageless?: boolean;
  // A catalogued entitlement id whose holders are granted this badge
  // automatically (#2061). Omitted when unlinked; the API answers an unknown
  // id with 400.
  linked_entitlement?: string;
  // `true` makes the badge automatic-only (#2268); it needs
  // `linked_entitlement` in the same request (400 otherwise).
  automatic_only?: boolean;
}

export interface UpdateBadgeInput {
  name?: string;
  description?: string;
  // `null` unlinks (stored as a field delete); omitting it leaves the link as
  // it is. A change takes effect at the next hourly reconcile, not inline.
  linked_entitlement?: string | null;
  // `true` sets automatic-only, `false` clears it (#2268); omitting it leaves
  // it. Unlinking an automatic-only badge needs `false` in the same request.
  automatic_only?: boolean;
}

interface BadgeResponse {
  badge: BadgeDefinition;
}

// Query keys
const badgeQueryKeys = {
  all: ['badges'] as const,
  adminList: (params?: { limit?: number; startAfter?: string }) =>
    ['badges', 'admin-list', params] as const,
  adminDetail: (badgeId: string) =>
    ['badges', 'admin-detail', badgeId] as const,
  publicList: (params?: { limit?: number; startAfter?: string }) =>
    ['badges', 'public-list', params] as const,
  publicDetail: (badgeId: string) =>
    ['badges', 'public-detail', badgeId] as const,
};

/**
 * Hook to fetch list of badges (admin endpoint)
 */
export function useAdminBadgesList(params?: {
  limit?: number;
  startAfter?: string;
}) {
  return useQuery({
    queryKey: badgeQueryKeys.adminList(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.startAfter) searchParams.set('startAfter', params.startAfter);
      const queryString = searchParams.toString();
      const endpoint = `/v1/admin/badges${queryString ? `?${queryString}` : ''}`;

      const response =
        await authenticatedApi.request<BadgeListResponse>(endpoint);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/** The admin list's page size, its maximum (`limit` is 1-500). */
const ADMIN_LIST_PAGE = 500;

/**
 * The whole admin catalogue, every page followed (#2349). The redeem group
 * editor needs all of it: a badge on a later page would otherwise have no
 * card, and a selected id it cannot find is reported as no longer existing.
 * Returns the list response shape with `hasMore: false`.
 */
export function useAllAdminBadges() {
  return useQuery({
    queryKey: [...badgeQueryKeys.all, 'admin-all'] as const,
    queryFn: async (): Promise<BadgeListResponse> => {
      const badges: BadgeDefinition[] = [];
      let startAfter: string | undefined;
      for (;;) {
        const searchParams = new URLSearchParams({
          limit: String(ADMIN_LIST_PAGE),
        });
        if (startAfter) searchParams.set('startAfter', startAfter);
        const response = await authenticatedApi.request<BadgeListResponse>(
          `/v1/admin/badges?${searchParams.toString()}`
        );
        const page = response.data;
        if (!page) throw new Error('Badge list response had no data');
        badges.push(...page.badges);
        if (!page.hasMore || page.badges.length === 0) break;
        startAfter = page.badges[page.badges.length - 1].badgeId;
      }
      return { badges, total: badges.length, hasMore: false };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch list of badges (public endpoint)
 */
export function useBadgesList(params?: {
  limit?: number;
  startAfter?: string;
}) {
  return useQuery({
    queryKey: badgeQueryKeys.publicList(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.startAfter) searchParams.set('startAfter', params.startAfter);
      const queryString = searchParams.toString();
      const endpoint = `/v1/badges${queryString ? `?${queryString}` : ''}`;

      const response =
        await authenticatedApi.request<BadgeListResponse>(endpoint);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single badge (public endpoint)
 */
export function usePublicBadge(badgeId: string) {
  return useQuery({
    queryKey: badgeQueryKeys.publicDetail(badgeId),
    queryFn: async () => {
      const response = await authenticatedApi.request<BadgeResponse>(
        `/v1/badges/${badgeId}`
      );
      return response.data;
    },
    enabled: !!badgeId,
  });
}

/**
 * Hook to fetch a single badge (admin endpoint)
 */
export function useBadge(badgeId: string) {
  return useQuery({
    queryKey: badgeQueryKeys.adminDetail(badgeId),
    queryFn: async () => {
      const response = await authenticatedApi.request<BadgeResponse>(
        `/v1/admin/badges/${badgeId}`
      );
      return response.data;
    },
    enabled: !!badgeId,
  });
}

/**
 * Hook to create a new badge
 */
export function useCreateBadge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (badge: CreateBadgeInput) => {
      const response = await authenticatedApi.request<BadgeResponse>(
        '/v1/admin/badges',
        {
          method: 'POST',
          body: JSON.stringify(badge),
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgeQueryKeys.all });
      toast.success('Badge created successfully');
    },
    onError: (error: any) => {
      // `ApiHttpError` carries the server's message on `.message`; there is
      // no `.response` on anything `authenticatedApi.request` throws.
      const message = error?.message || 'Failed to create badge';
      toast.error(String(message));
    },
  });
}

/**
 * Hook to update a badge
 */
export function useUpdateBadge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      badgeId,
      updates,
    }: {
      badgeId: string;
      updates: UpdateBadgeInput;
    }) => {
      const response = await authenticatedApi.request<BadgeResponse>(
        `/v1/admin/badges/${badgeId}`,
        {
          method: 'PUT',
          body: JSON.stringify(updates),
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: badgeQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.adminDetail(variables.badgeId),
      });
      toast.success('Badge updated successfully');
    },
    onError: (error: any) => {
      // `ApiHttpError` carries the server's message on `.message`; there is
      // no `.response` on anything `authenticatedApi.request` throws.
      const message = error?.message || 'Failed to update badge';
      toast.error(String(message));
    },
  });
}

/**
 * Hook to upload badge icon
 */
export function useUploadBadgeIcon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ badgeId, file }: { badgeId: string; file: File }) => {
      const response = await authenticatedApi.uploadBadgeIcon(badgeId, file);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to upload badge icon');
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: badgeQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: badgeQueryKeys.adminDetail(variables.badgeId),
      });
    },
    onError: (error: any) => {
      const message = error.message || 'Failed to upload badge icon';
      toast.error(String(message));
    },
  });
}

/**
 * Hook to delete a badge
 */
export function useDeleteBadge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (badgeId: string) => {
      await authenticatedApi.request(`/v1/admin/badges/${badgeId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgeQueryKeys.all });
      toast.success('Badge deleted successfully');
    },
    onError: (error: any) => {
      // `ApiHttpError` carries the server's message on `.message`; there is
      // no `.response` on anything `authenticatedApi.request` throws.
      const message = error?.message || 'Failed to delete badge';
      toast.error(String(message));
    },
  });
}

// Badge assignment types
export interface BadgeAssignment {
  userId: string;
  userDisplayName: string;
  badgeId: string;
  badgeName: string;
  earned_at: string;
  granted_by: string;
  hidden?: true;
}

interface UserBadgesResponse {
  user: {
    uid: string;
    display_name: string;
    email_verified: boolean;
  };
  badges: Array<{
    badgeId: string;
    earned_at: string;
    // `entitlement:<id>` for an automatic grant (#2061), otherwise manual.
    granted_by: string;
    // Admin view: a hidden assignment is listed, marked. Never false.
    hidden?: true;
    badge: BadgeDefinition;
  }>;
  total: number;
}

/**
 * `201` body of `POST /v1/admin/users/{uid}/badges:grant`. `converted` is
 * present (and true) only when the grant took over an automatic
 * `entitlement:*` grant (#2061), which then outlives the entitlement.
 */
export interface GrantBadgeResponse {
  userBadge: { badgeId: string; earned_at: string; granted_by: string };
  badge: BadgeDefinition;
  message: string;
  converted?: true;
}

// Assignment query keys
const assignmentQueryKeys = {
  userBadges: (userId: string) => ['badges', 'user', userId] as const,
  assignments: (params?: {
    badgeId?: string;
    grantedBy?: string;
    limit?: number;
  }) => ['badges', 'assignments', params] as const,
};

/** Refetch a user's badges and the assignments view after a change. */
function invalidateAssignments(queryClient: QueryClient, userId: string) {
  queryClient.invalidateQueries({
    queryKey: assignmentQueryKeys.userBadges(userId),
  });
  queryClient.invalidateQueries({
    queryKey: assignmentQueryKeys.assignments(),
  });
}

/**
 * Hook to grant a badge to a user
 */
export function useGrantBadge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      badgeId,
      reason,
    }: {
      userId: string;
      badgeId: string;
      reason?: string;
    }) => {
      const response = await authenticatedApi.request<GrantBadgeResponse>(
        `/v1/admin/users/${userId}/badges:grant`,
        {
          method: 'POST',
          body: JSON.stringify({ badgeId, reason }),
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      invalidateAssignments(queryClient, variables.userId);
      toast.success(
        data?.converted
          ? 'Badge is now a manual grant; it will outlive the entitlement'
          : 'Badge granted successfully'
      );
    },
    onError: (error: any) => {
      // Extract message from Error object (authenticated-api already parsed JSON)
      const message = error.message || 'Failed to grant badge';
      toast.error(message);
    },
  });
}

/**
 * Hook to revoke a badge from a user
 */
export function useRevokeBadge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      badgeId,
      reason,
    }: {
      userId: string;
      badgeId: string;
      reason?: string;
    }) => {
      // Strict percent-encoding, as for the manual-entitlement DELETE: an `&`
      // or `#` in free text would otherwise cut the value short. The spec also
      // accepts form encoding since #2034, but this does not rely on it.
      const query = reason ? `?reason=${encodeRfc3986(reason)}` : '';
      const endpoint = `/v1/admin/users/${encodeURIComponent(userId)}/badges/${encodeRfc3986(badgeId)}${query}`;

      await authenticatedApi.request(endpoint, {
        method: 'DELETE',
      });
    },
    onSuccess: (_, variables) => {
      invalidateAssignments(queryClient, variables.userId);
      toast.success('Badge revoked successfully');
    },
    onError: (error: any) => {
      // The revoke dialog displays 409 errors.
      if (error instanceof ApiHttpError && error.status === 409) return;
      // The server's refusal (e.g. a 400 for a reason over 500 characters)
      // reaches the admin as `ApiHttpError.message`.
      const message = error?.message || 'Failed to revoke badge';
      toast.error(String(message));
    },
  });
}

/** `200` body of `PATCH /v1/admin/users/{uid}/badges/{badgeId}` (#2061). */
export interface SetUserBadgeHiddenResponse {
  userBadge: {
    badgeId: string;
    earned_at: string;
    granted_by: string;
    hidden?: true;
  };
  // false when the badge was already in the requested state; nothing written.
  changed: boolean;
}

/**
 * Hook to hide or unhide a badge a user holds (#2061)
 *
 * A hidden badge stays assigned but is absent from `badge_ids`, so from the
 * downstream profile copy, and from both profile endpoints. Hiding does not
 * survive revocation: an automatic grant that is revoked and granted again
 * comes back visible.
 */
export function useSetUserBadgeHidden() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      badgeId,
      hidden,
      reason,
    }: {
      userId: string;
      badgeId: string;
      hidden: boolean;
      reason?: string;
    }) => {
      const response =
        await authenticatedApi.request<SetUserBadgeHiddenResponse>(
          `/v1/admin/users/${encodeURIComponent(userId)}/badges/${encodeRfc3986(badgeId)}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ hidden, ...(reason && { reason }) }),
          }
        );
      return response.data;
    },
    onSuccess: (_, variables) => {
      invalidateAssignments(queryClient, variables.userId);
      // The profile's `badges` drop or regain the id.
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
      toast.success(variables.hidden ? 'Badge hidden' : 'Badge unhidden');
    },
    onError: (error: any, variables) => {
      // The user, the assignment or the badge's definition is gone (#2431):
      // refetch, so a list loaded before stops offering the badge.
      if (error instanceof ApiHttpError && error.status === 404) {
        invalidateAssignments(queryClient, variables.userId);
      }
      const message = error?.message || 'Failed to change badge visibility';
      toast.error(String(message));
    },
  });
}

/**
 * Hook to fetch badges for a specific user
 */
export function useUserBadges(userId: string) {
  return useQuery({
    queryKey: assignmentQueryKeys.userBadges(userId),
    queryFn: async () => {
      const response = await authenticatedApi.request<UserBadgesResponse>(
        `/v1/admin/users/${userId}/badges`
      );
      return response.data;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch all badge assignments (audit view)
 */
export function useBadgeAssignments(params?: {
  badgeId?: string;
  grantedBy?: string;
  limit?: number;
  startAfter?: string;
}) {
  return useQuery({
    queryKey: assignmentQueryKeys.assignments(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.badgeId) searchParams.set('badgeId', params.badgeId);
      if (params?.grantedBy) searchParams.set('grantedBy', params.grantedBy);
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.startAfter) searchParams.set('startAfter', params.startAfter);
      const queryString = searchParams.toString();
      const endpoint = `/v1/admin/badges/assignments${queryString ? `?${queryString}` : ''}`;

      const response =
        await authenticatedApi.request<BadgeAuditResponse>(endpoint);
      return response.data;
    },
  });
}
